import React, { useState } from 'react';
import {
    Box,
    Stack,
    Menu,
    MenuItem,
    Checkbox,
    ListItemText,
    Chip,
    IconButton,
} from '@mui/material';
import {
    FilterList,
    DateRange,
    Work,
    BusinessCenter,
    Close as CloseIcon,
    KeyboardArrowDown
} from '@mui/icons-material';

const CandidateJobFilters = ({ selectedFilters, setSelectedFilters }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentFilter, setCurrentFilter] = useState('');

    const handleClick = (event, type) => {
        setAnchorEl(event.currentTarget);
        setCurrentFilter(type);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setCurrentFilter('');
    };

    const handleFilterSelect = (value) => {
        setSelectedFilters(prev => {
            const currentValues = prev[currentFilter] || [];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];

            return {
                ...prev,
                [currentFilter]: newValues
            };
        });
    };

    const clearAllFilters = () => {
        setSelectedFilters({
            postedDate: [],
            workType: [],
            jobType: []
        });
    };

    const getMenuItems = () => {
        switch (currentFilter) {
            case 'postedDate':
                return [
                    { value: 'today', label: 'Today' },
                    { value: 'last-3-days', label: 'Last 3 Days' },
                    { value: 'last-7-days', label: 'Last 7 Days' }
                ];
            case 'workType':
                return [
                    { value: 'remote', label: 'Remote' }
                ];
            case 'jobType':
                return [
                    { value: 'Full-Time', label: 'Full-Time' },
                    { value: 'W2 - Contract', label: 'W2 - Contract' },
                    { value: '1099contract', label: '1099-Contract' },
                    { value: 'Contract to Hire', label: 'Contract to Hire' },
                    { value: 'contractbased', label: 'Contract Based' },
                    { value: 'Contract - Independent', label: 'Contract - Independent' },
                    { value: 'Contract - Corp-to-Corp', label: 'Contract to Hire - Corp-to-Corp' },
                ];
            default:
                return [];
        }
    };

    const getButtonStartIcon = (type) => {
        switch (type) {
            case 'postedDate':
                return <DateRange fontSize="small" />;
            case 'workType':
                return <Work fontSize="small" />;
            case 'jobType':
                return <BusinessCenter fontSize="small" />;
            default:
                return null;
        }
    };

    const getChipLabel = (type, value) => {
        const menuItems = getMenuItems();
        const item = menuItems.find(item => item.value === value);
        return item ? item.label : value;
    };

    const handleChipDelete = (type, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [type]: prev[type].filter(v => v !== value)
        }));
    };

    return (
        <Box sx={{
            p: 1,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            width: '100%',
            boxSizing: 'border-box'
        }}>            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Box
                    onClick={(e) => handleClick(e, 'postedDate')}
                    sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: selectedFilters.postedDate?.length ? 'primary.main' : 'inherit',
                        '&:hover': {
                            backgroundColor: '#f5f5f5'
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    Date
                    <KeyboardArrowDown sx={{ fontSize: 16 }} />
                </Box>

                <Box
                    onClick={(e) => handleClick(e, 'workType')}
                    sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: selectedFilters.workType?.length ? 'primary.main' : 'inherit',
                        '&:hover': {
                            backgroundColor: '#f5f5f5'
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    Work Type
                    <KeyboardArrowDown sx={{ fontSize: 16 }} />
                </Box>

                <Box
                    onClick={(e) => handleClick(e, 'jobType')}
                    sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: selectedFilters.jobType?.length ? 'primary.main' : 'inherit',
                        '&:hover': {
                            backgroundColor: '#f5f5f5'
                        },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    Job Type
                    <KeyboardArrowDown sx={{ fontSize: 16 }} />
                </Box>
            </Stack>

            {Object.entries(selectedFilters).map(([type, values]) => values && values.length > 0 && (
                <Stack
                    key={type}
                    direction="row"
                    spacing={1}
                    sx={{
                        mb: 1,
                        flexWrap: 'wrap',
                        gap: 0.5,
                        '& > *': { my: 0.5 }
                    }}
                >
                    {values.map(value => (
                        <Chip
                            key={value} size="small"
                            label={value}
                            onDelete={() => handleChipDelete(type, value)}
                            sx={{
                                backgroundColor: '#f5f5f5',
                                fontSize: '11px',
                                height: '22px',
                                '& .MuiChip-deleteIcon': {
                                    fontSize: '14px'
                                }
                            }}
                        />
                    ))}
                </Stack>
            ))}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }} PaperProps={{
                    sx: {
                        mt: 0.5,
                        maxHeight: '80vh',
                        width: 250,
                        '& .MuiMenuItem-root': {
                            padding: '4px 8px',
                            minHeight: '32px'
                        }
                    }
                }}
            >                {currentFilter === 'postedDate' && [
                { label: 'Today', value: 'today' },
                { label: 'Last 3 Days', value: 'last-3-days' },
                { label: 'Last 7 Days', value: 'last-7-days' },
            ].map(({ label, value }) => (
                <MenuItem key={value} onClick={() => handleFilterSelect(value)}>
                    <Checkbox
                        checked={selectedFilters.postedDate?.includes(value)}
                        size="small"
                    />
                    <ListItemText primary={label} sx={{ '& .MuiTypography-root': { fontSize: '12px' } }} />
                </MenuItem>
            ))}

                {currentFilter === 'workType' && [
                    { label: 'Remote', value: 'remote' },
                    { label: 'On-site', value: 'onsite' },
                ].map(({ label, value }) => (
                    <MenuItem key={value} onClick={() => handleFilterSelect(value)}>
                        <Checkbox
                            checked={selectedFilters.workType?.includes(value)}
                            size="small"
                        />
                        <ListItemText primary={label} sx={{ '& .MuiTypography-root': { fontSize: '12px' } }} />
                    </MenuItem>
                ))}{currentFilter === 'jobType' && [
                    //{ value: 'Full-Time', label: 'Full-Time' },
                    { value: 'Contract', label: 'Contract' },
                    { value: 'W2 - Contract', label: 'W2 - Contract' },
                    { value: '1099-Contract', label: '1099-Contract' },
                    { value: 'Contract to Hire', label: 'Contract to Hire' },
                    { value: 'Contract Based', label: 'Contract Based' },
                    { value: 'Contract - Independent', label: 'Contract - Independent' },
                    { value: 'Contract to Hire - Corp-to-Corp', label: 'Contract to Hire - Corp-to-Corp' },
                    { value: 'Contract - Corp-to-Corp', label: 'Contract - Corp-to-Corp' },
                ].map(({ label, value }) => (
                    <MenuItem key={value} onClick={() => handleFilterSelect(value)}>
                        <Checkbox
                            checked={selectedFilters.jobType?.includes(value)}
                            size="small"
                        />
                        <ListItemText primary={label} sx={{ '& .MuiTypography-root': { fontSize: '12px' } }} />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default CandidateJobFilters;